import { findLastIndex, isEmpty, propEq } from 'ramda'
import Actions from '../actions'
import { clinicTypes, planTypes, routes } from '../consts'
import config from '../config.json'
import dayjs from 'dayjs'

const findPendingAppointmentIsSet = findLastIndex(
  propEq('type', 'pendingAppointmentIsSet')
)
const findChooseTime = findLastIndex(propEq('type', 'chooseTime'))

export const isLastMessageSholdBePaid = ({ chat }) => {
  const { messages } = chat

  // If there is pending appointment and the patient didn't pay, propmpt payment
  if (!messages?.length) return false

  const pendingAppointmentIsSetIndex = findPendingAppointmentIsSet(messages)
  if (pendingAppointmentIsSetIndex === messages.length - 1) return true
  const chooseTimeIndex = findChooseTime(messages)
  if (chooseTimeIndex === messages.length - 2) return true
}

export const isUnconfirmedAppointment = ({ appointments }) => {
  const allApplintments = appointments.appointments
  const unconfirmd = allApplintments.filter(
    appointment => appointment.status === 'pending'
  )
  return !!unconfirmd.length
}

export const isHaveSubscription = ({ payment }) => {
  const { my_subscriptions } = payment

  return my_subscriptions && !isEmpty(my_subscriptions)
}

export const getUserSubscription = (
  mySubscriptions,
  futureSubscriptions,
  selectedEnrollmentYear
) => {
  const allSubscriptions = [
    ...(mySubscriptions || []),
    ...(futureSubscriptions?.flat() || [])
  ]

  const subscription = getSubscriptionByEnrollmentYear(
    allSubscriptions,
    selectedEnrollmentYear
  )
  return subscription
}

export const isZuoraSubscription = ({ payment }) => {
  const { my_subscriptions } = payment

  return my_subscriptions?.[0]?.billing_provider === 'zuora'
}

export const isVisitsRemaining = ({ payment }) => {
  const { my_subscriptions } = payment

  return my_subscriptions?.[0]?.visits_remaining > 0
}

export const isVisitPriceZero = ({ payment }) => {
  const { my_subscriptions } = payment

  return my_subscriptions[0]?.plan_details.visit_price === '0'
}

export const isHaveCoupon = ({ payment }) => {
  const { coupons } = payment
  return !!coupons
}

export const isHaveCredit = ({ payment }) => {
  const { credits } = payment
  return !!credits
}

export const requestedPaymentMethodChange = ({ chat }) => {
  const { requestedPaymentMethodChange } = chat
  return !!requestedPaymentMethodChange
}

export const isFamilyPlan = state =>
  state.payment.selectedPlan.type === 'family'

export const cameFromEarliest = state =>
  state.appointments.selectedTimeOption === 'earliest'

export const isOnPlusPlan = mySubscriptions =>
  mySubscriptions?.[0].plan_details.is_reimbursement

export const isReimbersementEligiblePlan = plan =>
  plan.plan_details.is_reimbursement

export const planSmallImage = image_name => {
  return {
    uri: config.gcpStorageUrl + '/app/' + image_name + '.png'
  }
}

export const isNotAPrimarySubscription = plan =>
  !plan.clinic &&
  !plan.parent &&
  (plan.type === planTypes.ONE_TIME || plan.type === planTypes.MEDICAL_FILE)

export const isNotAMHSubscription = plan =>
  plan.clinic &&
  (plan.type === planTypes.ONE_TIME || plan.type === planTypes.MEDICAL_FILE)

export const isBlackListedStatePlanPrimary = (plan, userState) =>
  !plan.clinic && !plan.parent && !plan?.blackListedStates?.includes(userState)

export const isBlackListedStatePlanMH = (plan, userState) =>
  plan.clinic &&
  plan.clinic === clinicTypes.MENTAL &&
  !plan?.blackListedStates?.includes(userState)

export const calculateTotalPrice = (
  balancePerSubscription,
  paymentConfigPerSubscriptions
) => {
  if (!balancePerSubscription?.length || !paymentConfigPerSubscriptions)
    return 0

  return balancePerSubscription.reduce((total, subscription) => {
    const config = paymentConfigPerSubscriptions[subscription.subscriptionId]
    if (!config || config.excluded) return total

    let subscriptionTotal = 0
    // Always include overdue (as per requirement)
    if (config.includeOverdue && subscription.overduePrice) {
      subscriptionTotal += subscription.overduePrice
    }
    // Include due only if includeDue is true
    if (config.includeDue && subscription.duePrice) {
      subscriptionTotal += subscription.duePrice
    }
    return total + subscriptionTotal
  }, 0)
}

export const getExcludedSubscriptionIds = paymentConfigPerSubscriptions => {
  if (!paymentConfigPerSubscriptions) return []
  return Object.entries(paymentConfigPerSubscriptions)
    .filter(([, config]) => config.excluded)
    .map(([subscriptionId]) => subscriptionId)
}

// Get the subscription ids that we don't want to include their dues
export const getExcludedDueInvoicesOfSubscriptionsIds =
  paymentConfigPerSubscriptions => {
    if (!paymentConfigPerSubscriptions) return []
    return Object.entries(paymentConfigPerSubscriptions)
      .filter(([, config]) => !config.includeDue)
      .map(([subscriptionId]) => subscriptionId)
  }

export const getPlansActionAccordingToSubscription = my_subscriptions => {
  const billing_provider = my_subscriptions?.[0].billing_provider

  const isSubscribed = isHaveSubscription({ payment: { my_subscriptions } })
  const isStripe = billing_provider?.includes('stripe')

  return isSubscribed
    ? isStripe
      ? Actions.createAction(Actions.BILLING_PORTAL_REQUESTED)
      : Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.PROFILE_ROUTE.PAYMENT_DETAILS
        )
    : Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS)
}

export const isRenewingEnrollment = enrollmentYears => {
  return enrollmentYears?.length > 1
}

export const getSubscriptionByEnrollmentYear = (
  subscriptions = [],
  enrollmentYear
) => {
  return subscriptions.find(subscription => {
    if (subscription && subscription.current_period_start) {
      const subscriptionYear = dayjs(subscription.current_period_start).year()
      return subscriptionYear === enrollmentYear
    }
    return false
  })
}
export const getEarliestEnrollmentYear = enrollmentYears => {
  return enrollmentYears?.[0] || ''
}

export const getLatestEnrollmentYear = enrollmentYears => {
  return enrollmentYears?.[enrollmentYears.length - 1] || ''
}
